import { ApiFilterBase, ApiPagedData } from "@skailer-com/uikit/core";

export enum ApiWebhookEventType {
  time_off_request_approved = "time_off_request_approved",
  employee_hired = "employee_hired",
  employee_terminated = "employee_terminated",
  employee_position_changed = "employee_position_changed",
  employee_employment_changed = "employee_employment_changed",
  employee_compensation_changed = "employee_compensation_changed",
  task_completed = "task_completed",
  task_archived = "task_archived",
  workflow_execution_completed = "workflow_execution_completed",
  approval_request_approved = "approval_request_approved",
  approval_request_rejected = "approval_request_rejected",
  approval_request_canceled = "approval_request_canceled",
  survey_completed = "survey_completed",
  esign_envelope_completed = "esign_envelope_completed",
  employee_notification = "employee_notification",

  candidate_created = "candidate_created",
  candidate_stage_changed = "candidate_stage_changed",
  candidate_hired = "candidate_hired",
  candidate_disqualified = "candidate_disqualified",
  candidate_feedback_submitted = "candidate_feedback_submitted",
  offer_approved = "offer_approved",
  offer_rejected = "offer_rejected",

  job_created = "job_created",
  job_updated = "job_updated",
  job_status_changed = "job_status_changed",
  job_deleted = "job_deleted",
}

const webhookEventTitleKeys: Partial<Record<ApiWebhookEventType, string>> = {
  [ApiWebhookEventType.employee_compensation_changed]:
    "workflow:event_type_employee_compensation_changed",
  [ApiWebhookEventType.employee_hired]: "workflow:event_type_employee_hired",
  [ApiWebhookEventType.employee_position_changed]: "workflow:event_type_employee_position_changed",
  [ApiWebhookEventType.employee_terminated]: "workflow:event_type_employee_terminated",
  [ApiWebhookEventType.candidate_created]: "workflow:event_type_candidate_created",
  [ApiWebhookEventType.candidate_stage_changed]: "workflow:event_type_candidate_stage_changed",
  [ApiWebhookEventType.candidate_hired]: "workflow:event_type_candidate_hired",
  [ApiWebhookEventType.candidate_disqualified]: "workflow:event_type_candidate_disqualified",
  [ApiWebhookEventType.candidate_feedback_submitted]:
    "workflow:event_type_candidate_feedback_submitted",
  [ApiWebhookEventType.offer_approved]: "workflow:event_type_offer_approved",
  [ApiWebhookEventType.offer_rejected]: "workflow:event_type_offer_rejected",
};

export const getWebhookEventTypeTitleKey = (eventType: ApiWebhookEventType): string =>
  webhookEventTitleKeys[eventType] ?? `core:webhook_event_${eventType}`;

export interface ApiWebhook {
  id: number;
  title: string | null;
  description: string | null;
  url: string;
  is_active: boolean;
  event_types: ApiWebhookEventType[];
  headers: Record<string, string>;
  has_signature_secret: boolean;
  created_by: number | null;
  datetime_created_at: Date;
  datetime_updated_at: Date;
}

export interface ApiWebhookWithSecret extends ApiWebhook {
  signature_secret: string;
}

export interface ApiWebhookSave {
  title: string | null;
  description: string | null;
  url: string;
  is_active: boolean;
  event_types: ApiWebhookEventType[];
  headers: Record<string, string>;
}

export type ApiWebhookUpdate = Partial<ApiWebhookSave>;

export interface ApiWebhookFilter extends ApiFilterBase {
  is_active?: boolean | null;
  event_type?: ApiWebhookEventType | null;
  search_text?: string | null;
}

export type ApiWebhookPagedData = ApiPagedData<ApiWebhook>;

export const ApiWebhookFilterDefault: ApiWebhookFilter = {
  page_number: 1,
  search_text: "",
};

export enum ApiWebhookDeliveryStatus {
  pending = "pending",
  delivered = "delivered",
  failed = "failed",
}

export interface ApiWebhookDelivery {
  id: number;
  webhook_id: number;
  event_id: string;
  event_type: ApiWebhookEventType;
  request_url: string;
  request_headers: Record<string, unknown>;
  request_payload: Record<string, unknown>;
  response_status: number | null;
  response_headers: Record<string, unknown> | null;
  response_body: string | null;
  duration_ms: number | null;
  status: ApiWebhookDeliveryStatus;
  attempt_number: number;
  error_message: string | null;
  datetime_event_occurred_at: Date;
  datetime_created_at: Date;
  datetime_next_retry_at: Date | null;
  datetime_delivered_at: Date | null;
}

export interface ApiWebhookDeliveryFilter extends ApiFilterBase {
  webhook_id?: number | null;
  event_type?: ApiWebhookEventType | null;
  status?: ApiWebhookDeliveryStatus | null;
  period_start?: Date | null;
  period_end?: Date | null;
}

export interface ApiWebhookTestDeliveryResult {
  success: boolean;
  status_code: number;
  response_body: string | null;
  duration_ms: number | null;
  error_message: string | null;
}

export type ApiWebhookDeliveryPagedData = ApiPagedData<ApiWebhookDelivery>;
